/**
 * The allocation-conflict notice (#305) — "this truck / this person is
 * already committed elsewhere on these dates".
 *
 * It is a ROW-LEVEL INLINE NOTICE, not a Banner, and deliberately so: a
 * Banner would fail DESIGN-SYSTEM.md's dismissal contract in both directions. It is
 * not resolved-by-action (the Operator often *intends* the overlap — a truck
 * doing two short local jobs in a day is ordinary), and it is not
 * acknowledgeable-forever either, because the condition is live and per-row.
 * The precedent is the substitution notice on a gear row: same yellow, same
 * position under the item name, same voice.
 *
 * It warns; nothing it says blocks a save.
 */
import { Link } from "react-router";
import { Tooltip } from "../ui/Tooltip";
import { formatDateRange } from "../../lib/format-utils";
import type { AllocationClash } from "../../lib/allocation-api";
import { InlineBanner } from "../ui/InlineBanner";
import styles from "./AllocationConflictNotice.module.css";

/**
 * Availability is Company-wide. A multi-branch Company's fleet is not
 * partitioned by branch (#304), so Truck 3 at Hamilton and a job in Whangārei
 * read as the same availability question. That over-warns rather than
 * under-warns — the safe direction — but the reader is told.
 */
const SCOPE_HINT =
  "Availability is company-wide — vehicles and crew are not split by branch.";

export function AllocationConflictNotice({
  lead,
  clashes,
}: {
  /** "Also out on" for a Vehicle, "Also booked on" for a Contractor. */
  lead: string;
  clashes: AllocationClash[];
}) {
  if (clashes.length === 0) return null;
  return (
    <InlineBanner variant="warning" placement="cell">
      <Tooltip content={SCOPE_HINT}>{lead}</Tooltip>{" "}
      {clashes.map((c, i) => (
        <span key={c.booking_id}>
          {i > 0 && ", "}
          <Link to={`/events/${c.booking_id}`} className={styles.link}>
            {c.event_name || c.quote_number || c.client_name || "another event"}
          </Link>
          {c.event_date && (
            <span>
              {" "}
              ({formatDateRange(c.event_date, c.event_end_date, "short")})
            </span>
          )}
        </span>
      ))}
      .
    </InlineBanner>
  );
}
